/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * Tool disposition filter (MCP scopes only).
 */
export const Disposition = {
  ReadOnly: "read_only",
  Destructive: "destructive",
  Idempotent: "idempotent",
  OpenWorld: "open_world",
} as const;
/**
 * Tool disposition filter (MCP scopes only).
 */
export type Disposition = OpenEnum<typeof Disposition>;

/**
 * The kind of resource this selector targets.
 */
export const ResourceKind = {
  Project: "project",
  Mcp: "mcp",
  Org: "org",
  Environment: "environment",
  Skill: "skill",
  Assistant: "assistant",
  RiskPolicy: "risk_policy",
  Chat: "chat",
  Agent: "agent",
  Workload: "workload",
  Wildcard: "*",
} as const;
/**
 * The kind of resource this selector targets.
 */
export type ResourceKind = OpenEnum<typeof ResourceKind>;

/**
 * A constraint that narrows which resources a grant applies to.
 */
export type Selector = {
  /**
   * Tool disposition filter (MCP scopes only).
   */
  disposition?: Disposition | undefined;
  /**
   * Project filter (MCP, environment, and assistant scopes). When set with resource_id='*', grants access to every resource of the kind in the project.
   */
  projectId?: string | undefined;
  /**
   * The resource identifier, or '*' for all resources of this kind.
   */
  resourceId: string;
  /**
   * The kind of resource this selector targets.
   */
  resourceKind: ResourceKind;
  /**
   * Server URL filter (risk policy scopes only). Include the URI scheme, for example https://api.example.com.
   */
  serverUrl?: string | undefined;
  /**
   * Specific tool name filter (MCP scopes only).
   */
  tool?: string | undefined;
};

/** @internal */
export const Disposition$inboundSchema: z.ZodMiniType<Disposition, unknown> =
  openEnums.inboundSchema(Disposition);
/** @internal */
export const Disposition$outboundSchema: z.ZodMiniType<string, Disposition> =
  openEnums.outboundSchema(Disposition);

/** @internal */
export const ResourceKind$inboundSchema: z.ZodMiniType<ResourceKind, unknown> =
  openEnums.inboundSchema(ResourceKind);
/** @internal */
export const ResourceKind$outboundSchema: z.ZodMiniType<string, ResourceKind> =
  openEnums.outboundSchema(ResourceKind);

/** @internal */
export const Selector$inboundSchema: z.ZodMiniType<Selector, unknown> = z.pipe(
  z.object({
    disposition: z.optional(Disposition$inboundSchema),
    project_id: z.optional(z.string()),
    resource_id: z.string(),
    resource_kind: ResourceKind$inboundSchema,
    server_url: z.optional(z.string()),
    tool: z.optional(z.string()),
  }),
  z.transform((v) => {
    return remap$(v, {
      "project_id": "projectId",
      "resource_id": "resourceId",
      "resource_kind": "resourceKind",
      "server_url": "serverUrl",
    });
  }),
);
/** @internal */
export type Selector$Outbound = {
  disposition?: string | undefined;
  project_id?: string | undefined;
  resource_id: string;
  resource_kind: string;
  server_url?: string | undefined;
  tool?: string | undefined;
};

/** @internal */
export const Selector$outboundSchema: z.ZodMiniType<
  Selector$Outbound,
  Selector
> = z.pipe(
  z.object({
    disposition: z.optional(Disposition$outboundSchema),
    projectId: z.optional(z.string()),
    resourceId: z.string(),
    resourceKind: ResourceKind$outboundSchema,
    serverUrl: z.optional(z.string()),
    tool: z.optional(z.string()),
  }),
  z.transform((v) => {
    return remap$(v, {
      projectId: "project_id",
      resourceId: "resource_id",
      resourceKind: "resource_kind",
      serverUrl: "server_url",
    });
  }),
);

export function selectorToJSON(selector: Selector): string {
  return JSON.stringify(Selector$outboundSchema.parse(selector));
}
export function selectorFromJSON(
  jsonString: string,
): SafeParseResult<Selector, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => Selector$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'Selector' from JSON`,
  );
}
